import { forwardRef } from 'react';

import { Combobox } from '../Combobox/Combobox';

import { type CountrySelectProps } from './CountrySelect.types';
import { useCountrySelectLogic } from './useCountrySelectLogic';

/**
 * One country, out of the ISO 3166-1 alpha-2 list.
 *
 * A **kind of its own**, not a `combobox` every caller fills in: the code list
 * is platform law in `@aysar/contracts`, and a caller-supplied option array
 * would mean each surface re-deriving 249 rows, the name lookup and the
 * collation — three chances to disagree about what a country is called and what
 * order countries come in.
 *
 * It is a `Combobox` underneath and deliberately nothing more, so the filter,
 * the keyboard model, the empty state, `isReadOnly` and the RTL treatment are
 * the ones every other picker in the portal already has. The one thing it adds
 * is the list — which is the only thing that is special about a country.
 *
 * Presentation only; the list, its order and its copy are in the hook (U5).
 */
export const CountrySelect = forwardRef<HTMLButtonElement, CountrySelectProps>(
  function CountrySelect({ placeholder, searchPlaceholder, emptyLabel, ...rest }, ref) {
    const view = useCountrySelectLogic();

    return (
      <Combobox
        ref={ref}
        {...rest}
        options={view.options}
        // The caller may still override any of the three — a form that says
        // "country of residence" wants its own placeholder — but the default
        // says what is being chosen instead of "Choose…".
        placeholder={placeholder ?? view.placeholder}
        searchPlaceholder={searchPlaceholder ?? view.searchPlaceholder}
        emptyLabel={emptyLabel ?? view.emptyLabel}
      />
    );
  },
);
